import React, { useState } from "react";
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import Button from "../ui/Button";

interface ViewPitchDspModalProps {
  isOpen: boolean;
  onClose: () => void;
  pitch: {
    id: number;
    userId: number;
    artistId: number | null;
    artistName: string;
    releaseTitle: string;
    releaseDate: string;
    configuration: string;
    releaseUPC: string;
    focusTrackName: string;
    trackISRC: string;
    featuredArtists: string;
    featuredArtistURI: string;
    altTitles: string;
    shortDescription: string;
    longDescription: string;
    lyrics: string;
    songDescription: string;
    mainGenre: string;
    songMood: string;
    language: string;
    linkToListen: string;
    promoPhotos: string;
    images: any[] | null;
    status: string;
    companyName: string;
    createdAt: string;
    updatedAt: string;
    AmazonInfo: any;
    SpotifyInfo: any;
    DeezerInfo: any;
    TidalInfo: any;
    AudiomackInfo: any;
    SoundCloudInfo: any;
    AppleInfo: any;
    YoutubeInfo: any;
    SiriusXMInfo: any;
    contributors: any[];
    starred: boolean;
  };
}

const ViewPitchDspModal: React.FC<ViewPitchDspModalProps> = ({
  isOpen,
  onClose,
  pitch,
}) => {
  const [contributorsExpanded, setContributorsExpanded] = useState(false);
  const [lyricsExpanded, setLyricsExpanded] = useState(false);
  const [dspExpanded, setDspExpanded] = useState<{ [key: string]: boolean }>({});

  if (!isOpen) return null;

  const toggleDsp = (dspName: string) => {
    setDspExpanded(prev => ({ ...prev, [dspName]: !prev[dspName] }));
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const dspInfos = [
    { name: "Amazon Music", data: pitch.AmazonInfo },
    { name: "Spotify", data: pitch.SpotifyInfo },
    { name: "Deezer", data: pitch.DeezerInfo },
    { name: "Tidal", data: pitch.TidalInfo },
    { name: "Audiomack", data: pitch.AudiomackInfo },
    { name: "SoundCloud", data: pitch.SoundCloudInfo },
    { name: "Apple Music", data: pitch.AppleInfo },
    { name: "YouTube Music", data: pitch.YoutubeInfo },
    { name: "SiriusXM", data: pitch.SiriusXMInfo },
  ].filter(dsp => dsp.data && Object.keys(dsp.data).length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-white border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-black">
            {pitch.artistName} - {pitch.releaseTitle}
          </h2>
          <button
            onClick={onClose}
            className="p-1 transition rounded-full hover:bg-neutral-100"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6 text-neutral-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Artist & Song Details Section */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Artist Name</label>
              <p className="mt-1 text-base text-black">{pitch.artistName || "N/A"}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Release Title</label>
              <p className="mt-1 text-base text-black">{pitch.releaseTitle || "N/A"}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Focus Track Name</label>
              <p className="mt-1 text-base text-black">{pitch.focusTrackName || "N/A"}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Release Date</label>
              <p className="mt-1 text-base text-black">{formatDate(pitch.releaseDate)}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Configuration</label>
              <p className="mt-1 text-base text-black">{pitch.configuration || "N/A"}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Company Name</label>
              <p className="mt-1 text-base text-black">{pitch.companyName || "N/A"}</p>
            </div>
          </div>

          {/* UPC & ISRC Section */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Release UPC</label>
              <p className="mt-1 text-base text-black">{pitch.releaseUPC || "N/A"}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Track ISRC</label>
              <p className="mt-1 text-base text-black">{pitch.trackISRC || "N/A"}</p>
            </div>
          </div>

          {/* Genre, Language, Mood Section */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Main Genre</label>
              <p className="mt-1 text-base text-black">{pitch.mainGenre || "N/A"}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Language</label>
              <p className="mt-1 text-base text-black">{pitch.language || "N/A"}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Song Mood</label>
              <p className="mt-1 text-base text-black">{pitch.songMood || "N/A"}</p>
            </div>
          </div>

          {/* Featured Artists Section */}
          {pitch.featuredArtists && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-semibold text-neutral-600">Featured Artists</label>
                <p className="mt-1 text-base text-black">{pitch.featuredArtists}</p>
              </div>
              {pitch.featuredArtistURI && (
                <div>
                  <label className="block text-sm font-semibold text-neutral-600">Featured Artist URI</label>
                  <p className="mt-1 text-base text-black break-all">{pitch.featuredArtistURI}</p>
                </div>
              )}
            </div>
          )}

          {/* Alternative Titles */}
          {pitch.altTitles && (
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Alternative Titles</label>
              <p className="mt-1 text-base text-black">{pitch.altTitles}</p>
            </div>
          )}

          {/* Short Description */}
          {pitch.shortDescription && (
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Short Description</label>
              <p className="mt-1 text-base leading-relaxed text-black">{pitch.shortDescription}</p>
            </div>
          )}

          {/* Long Description */}
          {pitch.longDescription && (
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Long Description</label>
              <p className="mt-1 text-base leading-relaxed text-black">{pitch.longDescription}</p>
            </div>
          )}

          {/* Song Description */}
          {pitch.songDescription && (
            <div>
              <label className="block text-sm font-semibold text-neutral-600">Song Description</label>
              <p className="mt-1 text-base leading-relaxed text-black">{pitch.songDescription}</p>
            </div>
          )}

          {/* Contributors Section - Collapsible */}
          {pitch.contributors && pitch.contributors.length > 0 && (
            <div className="border border-neutral-200 rounded-lg">
              <button
                onClick={() => setContributorsExpanded(!contributorsExpanded)}
                className="flex items-center justify-between w-full p-4 text-left transition bg-neutral-50 hover:bg-neutral-100"
              >
                <span className="text-base font-semibold text-black">
                  Contributors ({pitch.contributors.length})
                </span>
                {contributorsExpanded ? (
                  <ChevronUpIcon className="w-5 h-5 text-neutral-600" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-neutral-600" />
                )}
              </button>
              {contributorsExpanded && (
                <div className="p-4 space-y-3 border-t border-neutral-200">
                  {pitch.contributors.map((contributor: any, index: number) => (
                    <div key={index} className="p-3 bg-neutral-50 rounded-md">
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {contributor.name && (
                          <div>
                            <span className="text-sm font-semibold text-neutral-600">Name: </span>
                            <span className="text-sm text-black">{contributor.name}</span>
                          </div>
                        )}
                        {contributor.role && (
                          <div>
                            <span className="text-sm font-semibold text-neutral-600">Role: </span>
                            <span className="text-sm text-black">{contributor.role}</span>
                          </div>
                        )}
                        {contributor.uri && (
                          <div className="md:col-span-2">
                            <span className="text-sm font-semibold text-neutral-600">URI: </span>
                            <span className="text-sm text-black break-all">{contributor.uri}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Lyrics Section - Collapsible */}
          {pitch.lyrics && (
            <div className="border border-neutral-200 rounded-lg">
              <button
                onClick={() => setLyricsExpanded(!lyricsExpanded)}
                className="flex items-center justify-between w-full p-4 text-left transition bg-neutral-50 hover:bg-neutral-100"
              >
                <span className="text-base font-semibold text-black">Lyrics</span>
                {lyricsExpanded ? (
                  <ChevronUpIcon className="w-5 h-5 text-neutral-600" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-neutral-600" />
                )}
              </button>
              {lyricsExpanded && (
                <div className="p-4 border-t border-neutral-200">
                  <p className="text-base leading-relaxed text-black whitespace-pre-wrap">
                    {pitch.lyrics}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Links Section */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {pitch.linkToListen && (
              <div>
                <label className="block text-sm font-semibold text-neutral-600">Listen Here</label>
                <a
                  href={pitch.linkToListen}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 text-base text-primary-700 hover:underline break-all"
                >
                  {pitch.linkToListen}
                </a>
              </div>
            )}
            {pitch.promoPhotos && (
              <div>
                <label className="block text-sm font-semibold text-neutral-600">Promo Photos</label>
                <a
                  href={pitch.promoPhotos}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 text-base text-primary-700 hover:underline break-all"
                >
                  {pitch.promoPhotos}
                </a>
              </div>
            )}
          </div>

          {/* All DSPs Section - Expandable */}
          {dspInfos.length > 0 && (
            <div className="border border-neutral-200 rounded-lg">
              <div className="p-4 bg-neutral-50">
                <h3 className="text-lg font-semibold text-black">All DSPs</h3>
              </div>
              <div className="divide-y divide-neutral-200">
                {dspInfos.map((dsp, index) => (
                  <div key={index}>
                    <button
                      onClick={() => toggleDsp(dsp.name)}
                      className="flex items-center justify-between w-full p-4 text-left transition hover:bg-neutral-50"
                    >
                      <span className="text-base font-semibold text-neutral-700">{dsp.name} Info</span>
                      {dspExpanded[dsp.name] ? (
                        <ChevronUpIcon className="w-5 h-5 text-neutral-600" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-neutral-600" />
                      )}
                    </button>
                    {dspExpanded[dsp.name] && (
                      <div className="p-4 bg-neutral-50">
                        <div className="grid grid-cols-1 gap-3">
                          {Object.entries(dsp.data).map(([key, value]: [string, any]) => (
                            <div key={key} className="flex flex-col">
                              <span className="text-sm font-semibold text-neutral-600 capitalize">
                                {key.replace(/([A-Z])/g, ' $1').trim()}:
                              </span>
                              <span className="mt-1 text-sm text-black">
                                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || 'N/A')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end p-6 bg-neutral-50 border-t border-neutral-200">
          <Button
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ViewPitchDspModal;
